import { useState, useEffect } from "react";
import { api, formatCurrency, formatPercent, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Plus, Pencil, Trash2, Loader2, Users, CalendarIcon, Percent, ListChecks, Target, Mail, MailX, Archive, ArchiveRestore, UserCheck } from "lucide-react";
import { SellerCriteriaDialog } from "@/components/SellerCriteriaDialog";
import { SellerObjectivesDialog } from "@/components/SellerObjectivesDialog";

export const SellersPage = () => {
  const { isAdmin } = useAuth();
  const [sellers, setSellers] = useState([]);
  const [commissionRates, setCommissionRates] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sellerToDelete, setSellerToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);
  
  const [formData, setFormData] = useState({ name: "", email: "", employee_id: "" });
  const [rateData, setRateData] = useState({ rate: 2, effective_date: new Date() });
  const [criteriaSeller, setCriteriaSeller] = useState(null);
  const [objectivesSeller, setObjectivesSeller] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [historySeller, setHistorySeller] = useState(null);
  const [employees, setEmployees] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sellersRes, ratesRes, empsRes] = await Promise.all([
        api.getSellers({ include_archived: showArchived }),
        api.getCommissionRates({ commission_type: "seller" }),
        api.getEmployees().catch(() => ({ data: [] })),
      ]);
      setSellers(sellersRes.data);
      setCommissionRates(ratesRes.data);
      setEmployees(empsRes.data || []);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const handleArchiveToggle = async (seller) => {
    try {
      if (seller.archived) {
        await api.unarchiveSeller(seller.id);
        toast.success(`${seller.name} réactivé`);
      } else {
        await api.archiveSeller(seller.id);
        toast.success(`${seller.name} archivé`);
      }
      fetchData();
    } catch {
      toast.error("Erreur lors de l'archivage");
    }
  };

  const handleDeleteRate = async (rateId) => {
    try {
      await api.deleteCommissionRate(rateId);
      toast.success("Taux supprimé");
      fetchData();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getSellerRate = (sellerId) => {
    const rates = commissionRates.filter(r => r.user_id === sellerId);
    if (rates.length === 0) return 0.02; // Default 2%
    return rates[0].rate;
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", employee_id: "" });
    setEditingSeller(null);
  };

  const openDialog = (seller = null) => {
    if (seller) {
      setEditingSeller(seller);
      setFormData({ name: seller.name, email: seller.email || "", employee_id: seller.employee_id || "" });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const openRateDialog = (seller) => {
    setSelectedSeller(seller);
    setRateData({ rate: getSellerRate(seller.id) * 100, effective_date: new Date() });
    setRateDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        email: formData.email || null,
        employee_id: formData.employee_id || null,
      };
      if (editingSeller) {
        await api.updateSeller(editingSeller.id, payload);
        toast.success("Vendeur mis à jour");
      } else {
        await api.createSeller(payload);
        toast.success("Vendeur créé");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleRateSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createCommissionRate({
        user_id: selectedSeller.id,
        rate: parseFloat(rateData.rate) / 100,
        effective_date: rateData.effective_date.toISOString().split("T")[0],
        commission_type: "seller"
      });
      toast.success("Taux de commission mis à jour");
      setRateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const openDeleteDialog = (seller) => {
    setSellerToDelete(seller);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!sellerToDelete) return;
    try {
      await api.deleteSeller(sellerToDelete.id);
      toast.success("Vendeur supprimé");
      setDeleteDialogOpen(false);
      setSellerToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="sellers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Vendeurs</h1>
          <p className="text-muted-foreground mt-1">
            Gérez votre équipe commerciale et leurs commissions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="show-archived-sellers-toggle">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4"
            />
            Afficher les archivés
          </label>
          <Button onClick={() => openDialog()} data-testid="add-seller-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nouveau vendeur
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 bg-primary/10 rounded-sm flex items-center justify-center shrink-0">
              <Percent className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-medium">Commission vendeur</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Par défaut, la commission vendeur est de <strong>2%</strong> du montant HT de chaque commande.
                <br />
                Vous pouvez modifier le taux pour chaque vendeur à partir d'une date spécifique.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sellers List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : sellers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Aucun vendeur</p>
            <Button className="mt-4" onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un vendeur
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sellers.map((seller) => {
            const rate = getSellerRate(seller.id);
            const sellerRates = commissionRates.filter(r => r.user_id === seller.id);
            const linkedEmployee = seller.employee_id
              ? employees.find((e) => e.id === seller.employee_id)
              : null;

            return (
              <Card key={seller.id} className={`card-hover ${seller.archived ? "opacity-60 border-dashed" : ""}`} data-testid={`seller-card-${seller.id}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="font-heading font-bold text-primary">
                          {seller.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span>{seller.name}</span>
                      {seller.archived && (
                        <Badge variant="outline" className="text-[10px] border-slate-400 text-slate-600" data-testid={`archived-badge-${seller.id}`}>
                          Archivé
                        </Badge>
                      )}
                    </div>
                    <Badge
                      variant="outline"
                      className={(seller.email && seller.email.trim()) ? "border-green-400 text-green-700 text-[10px]" : "border-amber-400 text-amber-700 text-[10px]"}
                      title={seller.email || "Aucun email défini — les alertes ne seront pas envoyées"}
                      data-testid={`email-badge-${seller.id}`}
                    >
                      {(seller.email && seller.email.trim()) ? <><Mail className="h-3 w-3 mr-1" /> Email ✓</> : <><MailX className="h-3 w-3 mr-1" /> Email ✗</>}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {linkedEmployee && (
                    <Badge
                      variant="outline"
                      className="border-indigo-300 bg-indigo-50 text-indigo-700 text-[11px] font-medium"
                      data-testid={`linked-employee-badge-${seller.id}`}
                      title="Vendeur rattaché à un salarié : les commissions apparaîtront dans sa fiche RH"
                    >
                      <UserCheck className="h-3 w-3 mr-1" />
                      Salarié lié : {linkedEmployee.first_name} {linkedEmployee.last_name}
                    </Badge>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Taux de commission actuel</p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-heading font-bold text-primary">
                        {formatPercent(rate)}
                      </span>
                      <span className="text-muted-foreground">du montant HT</span>
                    </div>
                  </div>
                  
                  {sellerRates.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-muted-foreground">Historique des taux</p>
                        <button
                          onClick={() => setHistorySeller(seller)}
                          className="text-[11px] text-primary underline-offset-4 hover:underline"
                          data-testid={`history-seller-${seller.id}`}
                        >
                          Voir tout ({sellerRates.length})
                        </button>
                      </div>
                      <div className="space-y-1">
                        {sellerRates.slice(0, 3).map((r) => (
                          <Badge key={r.id} variant="outline" className="mr-1">
                            {formatPercent(r.rate)} dès {formatDate(r.effective_date)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openRateDialog(seller)}
                      className="flex-1"
                      data-testid={`edit-rate-${seller.id}`}
                    >
                      <Percent className="h-4 w-4 mr-1" />
                      Modifier taux
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDialog(seller)}
                      data-testid={`edit-seller-${seller.id}`}
                      title="Modifier (nom, email & lien salarié)"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleArchiveToggle(seller)}
                      data-testid={`archive-seller-${seller.id}`}
                      title={seller.archived ? "Restaurer" : "Archiver (préserve l'historique)"}
                    >
                      {seller.archived
                        ? <ArchiveRestore className="h-4 w-4 text-emerald-600" />
                        : <Archive className="h-4 w-4 text-slate-500" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(seller)}
                      data-testid={`delete-seller-${seller.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCriteriaSeller(seller)}
                    className="w-full"
                    data-testid={`manage-criteria-${seller.id}`}
                  >
                    <ListChecks className="h-4 w-4 mr-1" />
                    Critères de commission
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setObjectivesSeller(seller)}
                    className="w-full"
                    data-testid={`manage-objectives-${seller.id}`}
                  >
                    <Target className="h-4 w-4 mr-1" />
                    Objectifs & Primes
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Seller Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingSeller ? "Modifier le vendeur" : "Nouveau vendeur"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom du vendeur</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Ex: Jean Dupont"
                data-testid="seller-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="seller-email">Email (pour alertes objectifs)</Label>
              <Input
                id="seller-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="jean.dupont@entreprise.fr"
                data-testid="seller-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="seller-employee">Lié au salarié <span className="text-muted-foreground">(optionnel)</span></Label>
              <Select
                value={formData.employee_id || "__none__"}
                onValueChange={(v) => setFormData({ ...formData, employee_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger id="seller-employee" data-testid="seller-employee-select">
                  <SelectValue placeholder="— Aucun lien —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Aucun lien —</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.last_name} {emp.first_name}
                      {emp.employee_type ? ` (${emp.employee_type})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                L'historique des commissions apparaîtra sur la fiche du salarié.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="seller-submit-btn">
                {editingSeller ? "Mettre à jour" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rate Dialog */}
      <Dialog open={rateDialogOpen} onOpenChange={setRateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              Modifier le taux de commission
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRateSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Vendeur : <strong>{selectedSeller?.name}</strong>
            </p>
            <div className="space-y-2">
              <Label htmlFor="rate">Nouveau taux (%)</Label>
              <Input
                id="rate"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={rateData.rate}
                onChange={(e) => setRateData({ ...rateData, rate: e.target.value })}
                required
                data-testid="rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Date d'effet</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(rateData.effective_date, "PPP", { locale: fr })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={rateData.effective_date}
                    onSelect={(d) => d && setRateData({ ...rateData, effective_date: d })}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRateDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="rate-submit-btn">
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le vendeur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer <strong>{sellerToDelete?.name}</strong> ?
              Cette action est irréversible. Les taux de commission associés seront également supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-seller-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SellerCriteriaDialog
        open={!!criteriaSeller}
        onOpenChange={(v) => { if (!v) setCriteriaSeller(null); }}
        seller={criteriaSeller}
        allSellers={sellers}
      />

      <SellerObjectivesDialog
        open={!!objectivesSeller}
        onOpenChange={(v) => { if (!v) setObjectivesSeller(null); }}
        seller={objectivesSeller}
      />

      {/* Dialog historique des taux de commission */}
      <Dialog open={!!historySeller} onOpenChange={(v) => { if (!v) setHistorySeller(null); }}>
        <DialogContent className="max-w-lg" data-testid="commission-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Historique des taux de commission — {historySeller?.name}
            </DialogTitle>
            <DialogDescription>
              Consultez l'ensemble des taux de commission appliqués à ce vendeur.
              Le taux marqué « Actuel » est celui qui s'applique aux nouvelles commandes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {historySeller && (() => {
              const rates = commissionRates
                .filter(r => r.user_id === historySeller.id)
                .sort((a, b) => (b.effective_date || "").localeCompare(a.effective_date || ""));
              if (rates.length === 0) {
                return <p className="text-sm text-muted-foreground py-4 text-center">Aucun taux enregistré — le taux par défaut (2%) s'applique.</p>;
              }
              return rates.map((r, idx) => (
                <div
                  key={r.id}
                  className={`flex items-center justify-between p-3 rounded border ${idx === 0 ? "bg-primary/5 border-primary/30" : "bg-muted/30"}`}
                  data-testid={`rate-history-${r.id}`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-lg font-bold text-primary">{formatPercent(r.rate)}</span>
                      {idx === 0 && <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px]">Actuel</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Effectif depuis le {formatDate(r.effective_date)}
                    </p>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteRate(r.id)}
                      data-testid={`delete-rate-${r.id}`}
                      title="Supprimer cette entrée"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ));
            })()}
          </div>
          <DialogFooter>
            <Button onClick={() => { setHistorySeller(null); if (isAdmin) openRateDialog(historySeller); }} variant="outline">
              <Percent className="h-4 w-4 mr-2" /> Ajouter un nouveau taux
            </Button>
            <Button onClick={() => setHistorySeller(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SellersPage;
